(() => {
  const g = window;
  const Core = g.GlossaryCore;
  if (!Core) return;

  if (!g.__GLOSSARY_GRID_INIT__) g.__GLOSSARY_GRID_INIT__ = new WeakSet();
  const initialized = g.__GLOSSARY_GRID_INIT__;

  const esc = (s) => String(s || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

  const normalize = (s) => String(s || '').toLowerCase().trim();

  const getParam = (name) => {
    try {
      return String(new URL(window.location.href).searchParams.get(name) || '').trim();
    } catch (e) {
      return '';
    }
  };

  const init = (root) => {
    if (!root || initialized.has(root)) return;
    initialized.add(root);

    const gridEl = root.querySelector('[data-grid]');
    const statusEl = root.querySelector('[data-status]');
    const moreBtn = root.querySelector('[data-load-more]');
    const filterLine = root.querySelector('[data-filterline]');
    const filterType = root.querySelector('[data-filter-type]');
    const filterBase = root.querySelector('[data-filter-base]');
    const filterTag = root.querySelector('[data-filter-tag]');
    const filterButtons = [...root.querySelectorAll('[data-glossary-filter-tag]')];
    const searchInput = root.querySelector('[data-glossary-search]');
    const searchForm = root.querySelector('[data-glossary-search-form]');
    const searchClear = root.querySelector('[data-glossary-search-clear]');
    const moreToggle = root.querySelector('[data-glossary-more]');
    const chipsWrap = root.querySelector('[data-glossary-chips]');
    const railImages = [...root.querySelectorAll('[data-glossary-rail-src]')];

    if (!gridEl) return;

    const desktopRail = window.matchMedia('(min-width: 901px)');
    const hydrateRailImages = () => {
      if (!desktopRail.matches) return;
      railImages.forEach((image) => {
        const src = image.dataset.glossaryRailSrc;
        if (!src) return;
        image.src = src;
        delete image.dataset.glossaryRailSrc;
      });
    };
    hydrateRailImages();
    desktopRail.addEventListener?.('change', hydrateRailImages);

    const token = (root.dataset.token || '').trim();
    const endpoint = (root.dataset.endpoint || '').trim();
    const snapshotUrl = (root.dataset.snapshotUrl || '').trim();
    const type = (root.dataset.metaobjectType || '').trim();

    if ((!snapshotUrl && (!token || !endpoint)) || !type) {
      if (statusEl) statusEl.textContent = 'Missing config';
      return;
    }

    const pageSize = Math.min(250, Math.max(10, parseInt(root.dataset.pageSize || '50', 10) || 50));
    const initialRenderSize = Math.max(1, parseInt(root.dataset.initialRender || '24', 10) || 24);
    const expandedGlossaryTypes = new Set([
      'fish_glossary_template',
      'coral_glossary_template',
      'invert_glossary_template'
    ]);
    const loadMoreBatchSize = expandedGlossaryTypes.has(type) ? 48 : initialRenderSize;
    const serverOffset = Math.max(0, parseInt(root.dataset.serverOffset || '0', 10) || 0);
    const serverRendered = root.dataset.serverRendered === 'true';
    const ssrCards = [...gridEl.querySelectorAll('[data-glossary-handle]')];
    const renderedHandles = new Set(ssrCards.map((card) => card.dataset.glossaryHandle || '').filter(Boolean));

    const entryPrefix = (root.dataset.entryUrlPrefix || '/pages').replace(/\/$/, '');

    const colFilter = normalize(root.dataset.typeCollectionHandle || '');
    const baseTag = normalize(root.dataset.baseTag || '');

    const datasetTag = normalize(root.dataset.tagParam || '');
    let currentTag = normalize(getParam('tag')) || datasetTag;
    let searchQuery = normalize(getParam('q'));

    const tagsKey = 'tags';
    const colKey = 'related_collection';
    const sciKey = 'scientific_name';
    const subtitleKeys = ['scientific_name', 'species'];
    const imgKey = 'image';
    const requiresImage = type === 'fish_glossary_template' || type === 'coral_glossary_template';

    const configuredNameKey = (root.dataset.nameFieldKey || '').trim();
    const nameKeyCandidates = [configuredNameKey, 'name', 'title', 'common_name']
      .map((k) => (k || '').trim())
      .filter(Boolean);
    const resolveName = (f) => {
      for (const key of nameKeyCandidates) {
        const v = f[key]?.value;
        if (v && String(v).trim()) return v;
      }
      return '';
    };

    const state = {
      after: null,
      hasNext: true,
      all: [],
      filtered: [],
      rendered: renderedHandles.size,
      loading: false,
      loaded: false,
      loadPromise: null,
      snapshotAttempted: false
    };

    const setStatus = (t) => { if (statusEl) statusEl.textContent = t || ''; };

    const showFilters = () => {
      if (!filterLine) return;
      let any = false;
      if (filterType) {
        filterType.hidden = !type;
        filterType.textContent = type || '';
        any = any || !!type;
      }
      if (filterBase) {
        filterBase.hidden = !baseTag;
        filterBase.textContent = baseTag || '';
        any = any || !!baseTag;
      }
      if (filterTag) {
        filterTag.hidden = !currentTag;
        filterTag.textContent = currentTag || '';
        any = any || !!currentTag;
      }
      filterLine.hidden = !any;
    };

    const updateFilterButtons = () => {
      if (!filterButtons.length) return;
      filterButtons.forEach((btn) => {
        const btnTag = normalize(btn.dataset.glossaryFilterTag || '');
        const active = btnTag === currentTag;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    };

    const updateUrl = () => {
      try {
        const url = new URL(window.location.href);
        if (currentTag) url.searchParams.set('tag', currentTag);
        else url.searchParams.delete('tag');
        if (searchQuery) url.searchParams.set('q', searchQuery);
        else url.searchParams.delete('q');
        window.history.replaceState({}, '', url.toString());
      } catch (e) {}
    };

    const NEUTRAL_DOT = '#3b82f6';
    const careDot = (v) => {
      const s = normalize(v);
      if (/easy|beginner/.test(s)) return '#22c55e';
      if (/moderate|medium|intermediate/.test(s)) return '#f5b945';
      if (/difficult|hard|expert|advanced/.test(s)) return '#ef4444';
      return NEUTRAL_DOT;
    };
    const tempDot = (v) => {
      const s = normalize(v);
      if (/peaceful/.test(s)) return '#22c55e';
      if (/semi/.test(s)) return '#f97316';
      if (/aggressive/.test(s)) return '#ef4444';
      return NEUTRAL_DOT;
    };
    const reefDot = (v) => {
      const s = normalize(v);
      if (/caution|maybe|with care/.test(s)) return '#f5b945';
      if (/^no$|not reef|unsafe/.test(s)) return '#ef4444';
      if (/yes|reef safe/.test(s)) return '#22c55e';
      return NEUTRAL_DOT;
    };

    const SPEC_ICONS = {
      care: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12h3l2-6 4 12 2-6 2 3h5"/></svg>',
      temperament: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8.5 14.5c1 1.3 2.2 2 3.5 2s2.5-.7 3.5-2"/><line x1="9" y1="9.5" x2="9.01" y2="9.5"/><line x1="15" y1="9.5" x2="15.01" y2="9.5"/></svg>',
      reef: '<svg viewBox="0 0 420.68 383.66" fill="currentColor" stroke="none" style="fill:currentColor;stroke:none"><path d="M208.07,367.92c-11.86-.28-14.6-15.88-14.59-25.69-.2-10.38-.03-20.83.76-30.95.71-8.55,2.08-16.64,4.36-25.73,2.98-9.63,2.95-19.17-3.38-27.45-18.64-27.71-45.6-48.11-72.58-67.3-3.38-2.39-7.06-4.66-10.94-5.83-5.26-1.69-10.92-1.27-16.36-.71-9.76.92-19.54,1.95-29.23,2.44-11.58.11-27.39,1.74-37.29-4.54-6.26-4.07-7.49-13.47-1.9-18.68,4.11-4.15,10.4-5.24,16.08-5.45,6.46-.97,44.23,3.72,43.34-5.55,0-2.36-1.53-5.98-3.15-10.7-2.41-6.79-4.88-13.82-7.32-20.71-2.66-8.34-7.64-19.26-8.62-26.92-1.01-10.81,10.96-19.76,20.17-12.25,6.9,5.98,8.86,16.12,12.12,24.36,3.64,10.75,10.15,21.41,16.49,31.44,7.26,10.59,18.58,30.48,29.49,11.51,5.04-8.55,8.64-20.39,14.67-28.24,5.44-7.89,15.75-7.6,21.35-.4,9.31,12.63-4.11,33.75-10.63,46.46-5.97,10-8.61,15.5,3.52,21.58,15.04,8.66,27.11,20.56,37.21,35.11,4.56,6.04,9.97,16.85,17.75,8.19,20.7-20.49,33.89-47.87,37.61-77.52,1.49-8.94-2.2-16.31-9.65-21.22-15.64-10.83-29.96-22.18-41.32-37.4-5.26-6.99-13.35-16.12-13.25-23.97-.09-11.13,12.01-17.04,21.17-10.87,5.11,3.13,9.02,8.61,13.5,13.57,4.57,5.2,9.51,10.1,14.5,14.81,15.09,14.88,18.45,14.56,24.97-5.55,4.69-14.46,11.87-28.26,20.43-41.36,7.19-10.1,13.73-23.1,25.77-27.05,7.28-2.28,15.59,2.11,16.99,9.77,1.97,8.96-5.45,17.72-10.11,24.81-2.72,3.89-4.59,9.51-.34,10.75,13.96,2.47,33.54-14.69,48.04-16.94,11.58-2.97,21.6,9.23,14.58,19.61-6.37,8.7-17.74,13.96-27.1,18.97-16.41,8.19-32.22,13.42-47.37,15.74-10.76,1.23-16.5,5.84-18.54,16.68-2.12,10.21-7.49,41.01-2.75,50.39,4.89,8.38,16.56,6.58,24.87,6.98,10.58.29,23.84-1.73,32.47,5.55,3.98,4.06,4.31,11.23,1.33,16.04-2.61,4.12-7.43,6.15-14.04,7.55-15.66,3.34-33.47.6-48.5-.23-10.59.37-12.07,12.95-17.71,20.07-4.74,7.03-10.54,13.46-16.02,19.95-18.74,21.33-31.79,49.48-35.88,76.88-1.38,8.81-1.15,17.64-1.1,26.6.32,9.33-2.94,22.86-13.69,23.39h-.15Z"/></svg>',
      diet: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M7 2v9"/><path d="M5 2v5a2 2 0 0 0 2 2h0a2 2 0 0 0 2-2V2"/><path d="M7 11v11"/><path d="M16 2c-1.5 0-3 1.5-3 4.5S14.5 11 16 11"/><path d="M16 11v11"/></svg>',
      tank: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 2 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.3 7 12 12 20.7 7"/><line x1="12" y1="22" x2="12" y2="12"/></svg>',
      max: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 14l11-11 7 7-11 11z"/><path d="M7 10l1.5 1.5"/><path d="M9 8l2 2"/><path d="M11 6l1.5 1.5"/><path d="M13 4l2 2"/></svg>',
      lighting: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>',
      flow: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M2 6c2 0 2 2 4 2s2-2 4-2 2 2 4 2 2-2 4-2 2 2 4 2"/><path d="M2 12c2 0 2 2 4 2s2-2 4-2 2 2 4 2 2-2 4-2 2 2 4 2"/><path d="M2 18c2 0 2 2 4 2s2-2 4-2 2 2 4 2 2-2 4-2 2 2 4 2"/></svg>',
      placement: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>'
    };

    const SOFT = '\u00AD';
    const riskDot = (v) => {
      const n = parseInt(String(v).replace(/[^0-9]/g, ''), 10);
      if (!Number.isNaN(n)) {
        if (n <= 2) return '#22c55e';
        if (n === 3) return '#f5b945';
        return '#ef4444';
      }
      const s = normalize(v);
      if (/low|minor|harmless/.test(s)) return '#22c55e';
      if (/high|severe|major/.test(s)) return '#ef4444';
      return '#f5b945';
    };

    const buildSpecs = (f) => {
      const val = (k) => String(f[k]?.value || '').trim();
      const out = [];
      if (type === 'pest_id') {
        const risk = val('risk_level');
        const spread = val('spread_speed');
        const size = val('size');
        const found = val('found_on');
        if (risk) out.push({ icon: 'reef', label: 'Risk', value: /^\d+$/.test(risk) ? `Level ${risk}` : risk, dot: riskDot(risk) });
        if (spread) out.push({ icon: 'flow', label: 'Spread', value: spread, dot: NEUTRAL_DOT });
        if (size) out.push({ icon: 'max', label: 'Size', value: size, dot: NEUTRAL_DOT });
        if (found) out.push({ icon: 'placement', label: 'Found On', value: found, dot: NEUTRAL_DOT });
        return out;
      }
      if (type === 'invert_glossary_template') {
        const care = val('care_level') || val('care');
        const temperament = val('temperament');
        const reef = val('reef_safe');
        const diet = val('diet') || val('quick_diet');
        if (care) out.push({ icon: 'care', label: 'Care Level', value: care, dot: careDot(care) });
        if (temperament) out.push({ icon: 'temperament', label: 'Temper' + SOFT + 'ament', value: temperament, dot: tempDot(temperament) });
        if (reef) out.push({ icon: 'reef', label: 'Reef Safe', value: reef, dot: reefDot(reef) });
        if (diet) out.push({ icon: 'diet', label: 'Diet', value: diet, dot: NEUTRAL_DOT });
        return out;
      }
      if (type === 'coral_glossary_template') {
        const care = val('care_level');
        const lighting = val('lighting');
        const flow = val('flow');
        const placement = val('placement');
        if (care) out.push({ icon: 'care', label: 'Care', value: care, dot: careDot(care) });
        if (lighting) out.push({ icon: 'lighting', label: 'Lighting', value: lighting, dot: NEUTRAL_DOT });
        if (flow) out.push({ icon: 'flow', label: 'Flow', value: flow, dot: NEUTRAL_DOT });
        if (placement) out.push({ icon: 'placement', label: 'Placement', value: placement, dot: NEUTRAL_DOT });
        return out;
      }
      const care = val('care') || val('care_level');
      const temperament = val('temperament');
      const reef = val('reef_safe');
      const diet = val('quick_diet');
      const tank = val('min_tank_size');
      const max = val('max_size');
      if (care) out.push({ icon: 'care', label: 'Care Level', value: care, dot: careDot(care) });
      if (temperament) out.push({ icon: 'temperament', label: 'Temper' + SOFT + 'ament', value: temperament, dot: tempDot(temperament) });
      if (reef) out.push({ icon: 'reef', label: 'Reef Safe', value: reef, dot: reefDot(reef) });
      if (diet) out.push({ icon: 'diet', label: 'Diet', value: diet, dot: NEUTRAL_DOT });
      if (tank) out.push({ icon: 'tank', label: 'Tank Size', value: tank, dot: NEUTRAL_DOT });
      if (max) out.push({ icon: 'max', label: 'Max Size', value: max, dot: NEUTRAL_DOT });
      return out;
    };

    const matchNode = (node) => {
      const f = Core.nodeToFieldMap(node);
      const tags = Core.parseTags(f[tagsKey]?.value);
      const col = normalize(f[colKey]?.reference?.handle || '');

      // Page-level scoping (applied once at load). Category + text search are
      // applied client-side so the controls feel instant across the full set.
      if (colFilter || baseTag) {
        const collectionMatch = !!colFilter && col === colFilter;
        const tagMatch = !!baseTag && tags.includes(baseTag);
        if (!collectionMatch && !tagMatch) return null;
      }

      const img = f[imgKey]?.reference?.image;
      if (requiresImage && !img?.url) return null;

      const name = resolveName(f) || node.handle;
      let sci = '';
      for (const key of subtitleKeys) {
        const v = f[key]?.value;
        if (v && String(v).trim()) { sci = v; break; }
      }

      return {
        handle: node.handle,
        url: node.onlineStoreUrl || `${entryPrefix}/${node.handle}`,
        name,
        sci,
        tags,
        imgUrl: img?.url || '',
        imgAlt: img?.altText || '',
        specs: buildSpecs(f),
        searchText: normalize([name, sci, tags.join(' '), node.handle].join(' '))
      };
    };

    const specPill = (s) => `
          <div class="glossary-card-specs__pill">
            <span class="glossary-card-specs__icon" aria-hidden="true">${SPEC_ICONS[s.icon] || ''}</span>
            <span class="glossary-card-specs__label">${esc(s.label)}</span>
            <strong class="glossary-card-specs__value">${esc(s.value)}</strong>
            <span class="glossary-card-specs__dot" style="background:${esc(s.dot)};" aria-hidden="true"></span>
          </div>`;

    const imageUrl = (url, width) => {
      if (!url) return '';
      try {
        const parsed = new URL(url, window.location.origin);
        parsed.searchParams.set('width', String(width));
        return parsed.toString();
      } catch (e) {
        const joiner = String(url).includes('?') ? '&' : '?';
        return `${url}${joiner}width=${width}`;
      }
    };

    const cardHTML = (item) => `
      <a class="glossary-card" href="${esc(item.url)}" data-glossary-handle="${esc(item.handle)}">
        <div class="glossary-card-media">
          ${item.imgUrl ? `<img
            src="${esc(imageUrl(item.imgUrl, 600))}"
            srcset="${esc(imageUrl(item.imgUrl, 320))} 320w, ${esc(imageUrl(item.imgUrl, 480))} 480w, ${esc(imageUrl(item.imgUrl, 600))} 600w"
            sizes="(max-width: 640px) calc(100vw - 20px), (max-width: 990px) 50vw, 25vw"
            alt="${esc(item.imgAlt || item.name)}"
            loading="lazy">` : ''}
        </div>
        <div class="glossary-card-body">
          <h3 class="glossary-card-title">${esc(item.name)}</h3>
          ${item.sci ? `<p class="glossary-card-subtitle">${esc(item.sci)}</p>` : ''}
          ${item.specs && item.specs.length ? `<div class="glossary-card-specs"><div class="glossary-card-specs__grid">${item.specs.map(specPill).join('')}</div></div>` : ''}
        </div>
      </a>`;

    const fetchNextPage = async () => {
      if (snapshotUrl && !state.snapshotAttempted) {
        state.snapshotAttempted = true;
        try {
          const response = await fetch(snapshotUrl, {
            mode: 'cors',
            credentials: 'omit',
            headers: { Accept: 'application/json' }
          });
          const payload = await response.json().catch(() => null);
          if (
            !response.ok ||
            !payload ||
            payload.metaobjectType !== type ||
            !Array.isArray(payload.entries)
          ) {
            throw new Error(payload?.message || 'Invalid glossary snapshot');
          }

          state.after = null;
          state.hasNext = false;
          for (const node of payload.entries) {
            const matched = matchNode(node);
            if (matched) state.all.push(matched);
          }
          return;
        } catch (error) {
          if (!token || !endpoint) throw error;
        }
      }

      const json = await Core.sfFetch({
        endpoint,
        token,
        query: Core.METAOBJECTS_QUERY,
        variables: { type, first: pageSize, after: state.after }
      });

      const edges = json?.data?.metaobjects?.edges || [];
      const pageInfo = json?.data?.metaobjects?.pageInfo || {};

      state.after = pageInfo.endCursor || null;
      state.hasNext = !!pageInfo.hasNextPage;

      for (const e of edges) {
        const node = e?.node;
        if (!node) continue;
        const matched = matchNode(node);
        if (matched) state.all.push(matched);
      }
    };

    const computeFiltered = () => {
      state.filtered = state.all.filter((i) => matchesCategory(i) && matchesSearch(i));
    };

    const loadAll = () => {
      if (state.loaded) return Promise.resolve();
      if (state.loadPromise) return state.loadPromise;
      state.loading = true;
      state.loadPromise = (async () => {
        try {
          while (state.hasNext) await fetchNextPage();
          state.loaded = true;
        } finally {
          state.loading = false;
          state.loadPromise = null;
        }
      })();
      return state.loadPromise;
    };

    const matchesCategory = (item) => !currentTag || item.tags.includes(currentTag);
    const matchesSearch = (item) => {
      if (!searchQuery) return true;
      const terms = searchQuery.split(/\s+/).filter(Boolean);
      return terms.every((t) => item.searchText.includes(t));
    };

    const updateMore = () => {
      if (!moreBtn) return;
      if (serverOffset > 0) {
        moreBtn.hidden = true;
        return;
      }
      const hasUnrendered = state.filtered.some((item) => !renderedHandles.has(item.handle));
      moreBtn.hidden = !hasUnrendered && !state.hasNext;
    };

    const renderMore = (limit = initialRenderSize) => {
      const slice = state.filtered
        .filter((item) => !renderedHandles.has(item.handle))
        .slice(0, limit);
      if (slice.length) {
        gridEl.insertAdjacentHTML('beforeend', slice.map(cardHTML).join(''));
        slice.forEach((item) => renderedHandles.add(item.handle));
        state.rendered += slice.length;
      }
      updateMore();
    };

    const emptyMessage = () => {
      if (searchQuery) return `No results for “${searchQuery}”`;
      if (currentTag) return 'No results in this category';
      return 'No matches';
    };

    const applyFilters = () => {
      computeFiltered();
      gridEl.innerHTML = '';
      renderedHandles.clear();
      state.rendered = 0;

      if (!state.filtered.length) {
        setStatus(state.loaded ? emptyMessage() : 'Searching…');
      } else {
        setStatus('');
      }

      renderMore();
    };

    const syncSearchUI = () => {
      if (searchClear) searchClear.hidden = !searchQuery;
    };

    const runInteractiveFilter = async () => {
      setStatus('Searching…');
      try {
        await loadAll();
        applyFilters();
      } catch (e) {
        setStatus(e?.message || 'Interactive search is temporarily unavailable');
      }
    };

    showFilters();
    updateFilterButtons();
    if (searchInput) searchInput.value = getParam('q');
    syncSearchUI();
    setStatus(ssrCards.length ? '' : 'Loading…');

    const shouldDeferPrime = serverRendered && ssrCards.length && !currentTag && !searchQuery;
    const primePromise = (shouldDeferPrime
      ? Promise.resolve().then(() => {
        state.rendered = ssrCards.length;
        setStatus('');
        updateMore();
      })
      : (async () => {
        await fetchNextPage();
        if (!state.hasNext) state.loaded = true;
        computeFiltered();

        if (colFilter || baseTag || currentTag || searchQuery) {
          await loadAll();
          applyFilters();
          return;
        }

        if (serverRendered && ssrCards.length) {
          state.rendered = ssrCards.length;
          setStatus('');
          updateMore();
          return;
        }

        applyFilters();
      })()
    ).catch((e) => {
      setStatus(e?.message || 'Interactive search is temporarily unavailable');
      if (moreBtn) moreBtn.hidden = true;
    });

    if (moreBtn) {
      moreBtn.addEventListener('click', async () => {
        await primePromise;
        try {
          let available = state.filtered.filter((item) => !renderedHandles.has(item.handle)).length;
          while (state.hasNext && available < loadMoreBatchSize) {
            await fetchNextPage();
            computeFiltered();
            available = state.filtered.filter((item) => !renderedHandles.has(item.handle)).length;
          }
          if (!state.hasNext) state.loaded = true;
          renderMore(loadMoreBatchSize);
        } catch (e) {
          setStatus(e?.message || 'Unable to load more profiles');
        }
      });
    }

    filterButtons.forEach((btn) => {
      btn.addEventListener('click', async () => {
        await primePromise;
        const nextTag = normalize(btn.dataset.glossaryFilterTag || '');
        if (nextTag === currentTag && currentTag) currentTag = '';
        else currentTag = nextTag;

        updateUrl();
        showFilters();
        updateFilterButtons();
        await runInteractiveFilter();
      });
    });

    if (moreToggle && chipsWrap) {
      moreToggle.addEventListener('click', () => {
        const expanded = chipsWrap.classList.toggle('is-expanded');
        moreToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    }

    if (searchInput) {
      let debounce;
      const runSearch = async () => {
        await primePromise;
        searchQuery = normalize(searchInput.value);
        updateUrl();
        syncSearchUI();
        await runInteractiveFilter();
      };
      searchInput.addEventListener('input', () => {
        window.clearTimeout(debounce);
        debounce = window.setTimeout(runSearch, 120);
      });
    }

    if (searchForm) {
      searchForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        await primePromise;
        if (searchInput) searchQuery = normalize(searchInput.value);
        updateUrl();
        syncSearchUI();
        await runInteractiveFilter();
      });
    }

    if (searchClear) {
      searchClear.addEventListener('click', async () => {
        await primePromise;
        if (searchInput) searchInput.value = '';
        searchQuery = '';
        updateUrl();
        syncSearchUI();
        await runInteractiveFilter();
        if (searchInput) searchInput.focus();
      });
    }
  };

  const boot = () => {
    document.querySelectorAll('[data-glossary-grid]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const roots = e.target.matches?.('[data-glossary-grid]')
      ? [e.target]
      : (e.target.querySelectorAll?.('[data-glossary-grid]') || []);
    roots.forEach(init);
  });
})();
